<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :ajax-params="ajaxParams"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :showHeader="showTableHeader"
            :pagination="paginateTable"
            ajax-path="powerdns/domains"
            @ajaxResponse="handleAjaxResponse"
        />
        <app-modal
            :show.sync="showCreateModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Migrate to Route53</template>
            <template slot="body">
                <app-input
                    v-model="createForm.name"
                    placeholder="Domain Name"
                    :error="createForm.errors.get('name')"
                    @updated="createForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="createForm.delegation_set_id"
                    placeholder="Delegation Set"
                    :error="createForm.errors.get('delegation_set_id')"
                    @updated="createForm.errors.clear('delegation_set_id')"
                    :options="delegationSets"
                    label-key="caller_reference"
                    value-key="id"
                />
                <app-input
                    v-model="createForm.caller_reference"
                    placeholder="Caller Reference"
                    :error="createForm.errors.get('caller_reference')"
                    @updated="createForm.errors.clear('caller_reference')"
                />
                <app-input
                    v-model="createForm.comment"
                    placeholder="Comment"
                    :error="createForm.errors.get('comment')"
                    @updated="createForm.errors.clear('comment')"
                />                
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateModal = false"
                        :disable="creatingHostedZone"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createHostedZone"
                        :loading="creatingHostedZone"
                    >Migrate</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showRecordsModal"
            width="lg:w-4/5"
        >
            <template slot="header">{{ selected.name }} Records</template>
            <template slot="body">
                <tabulator
                    :columns="[
                        {
                            title: 'Name',
                            field: 'name',
                            sorter: 'string',
                            minWidth: 200,
                            headerSortStartingDir: 'desc',
                        },
                        {
                            title: 'Type', 
                            field: 'type', 
                            sorter: 'string',
                            headerSort: false,
                            width: 100,
                        },
                        {
                            title: 'Value',
                            field: 'content',
                            sorter: 'string',
                            minWidth: 300,
                            formatter: 'html',
                            variableHeight: true,
                        },
                        {
                            title: 'TTL',
                            field: 'ttl',
                            width: 100,
                        },
                    ]"
                    :actions="recordsActions"
                    :ajax-params="recordsAjaxParams"
                    :refresh.sync="refreshRecordsTable"
                    ajax-path="powerdns/records"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateRecordModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Create Record Set</template>
            <template slot="body">
                <app-input
                    v-model="createRecordForm.name"
                    placeholder="Name"
                    :error="createRecordForm.errors.get('name')"
                    @updated="createRecordForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="createRecordForm.type"
                    placeholder="Type"
                    :error="createRecordForm.errors.get('type')"
                    @updated="createRecordForm.errors.clear('type')"
                    :options="recordTypeOptions"
                    label-key="label"
                    value-key="value"
                    required
                />
                <app-input
                    v-model="createRecordForm.ttl"
                    placeholder="TTL (seconds)"
                    :error="createRecordForm.errors.get('ttl')"
                    @updated="createRecordForm.errors.clear('ttl')"
                />
                <app-textarea
                    v-model="createRecordForm.content"
                    placeholder="Value"
                    :error="createRecordForm.errors.get('content')"
                    @updated="createRecordForm.errors.clear('content')"
                    required
                />
                <div 
                    v-if="createRecordForm.type === 'A'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">IPv4 address. Enter multiple addresses on separate lines.</div>
                    <div class="flex-1 ml-5">Example:<br>
                        192.0.2.235<br>
                        198.51.100.234</div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'CNAME'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name that you want to resolve to instead of the value in the Name field.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        www.example.com
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'MX'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">A priority and a domain name that specifies a mail server. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                        [priority] [mail server host name]<br>
                        Example:<br>
                        10 mailserver.example.com.<br>
                        20 mailserver2.example.com.
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'AAAA'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">IPv6 address. Enter multiple addresses on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        2001:0db8:85a3:0:0:8a2e:0370:7334<br>
                        fe80:0:0:0:202:b3ff:fe1e:8329
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'TXT'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">A text record. Enter multiple values on separate lines. Enclose text in quotation marks.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        "Sample Text Entries"<br>
                        "Enclose entries in quotation marks"
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'PTR'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name that you want to return.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        www.example.com
                    </div>
                </div>                    
                <div 
                    v-if="createRecordForm.type === 'SRV'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An SRV record. For information about SRV record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [priority] [weight] [port] [server host name]<br>
                        Example:<br>
                            1 10 5269 xmpp-server.example.com.<br>
                            2 12 5060 sip-server.example.com.
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'SPF'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An SPF record. For information about SPF record format, refer to the applicable documentation. Enter multiple values on separate lines. Enclose values in quotation marks.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        "v=spf1 ip4:192.168.0.1/16-all"
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'NAPTR'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An NAPTR record. For information about NAPTR record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [order] [preference] [flags] [services] [regexp] [replacement]<br>
                        Example:<br>
                            100 100 "U" "" "!^.*$!sip:info@bar.example.com!" .<br>
                            10 100 "S" "SIP+D2U" "" foo.example.com.
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'CAA'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">Values that specify the certificate authorities that can issue certificates for this domain. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [flag] [tag] [value]<br>
                        Examples:<br>
                            0 issue "caa.example.com"<br>
                            0 issuewild ";"
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'NS'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name of a name server. Enter multiple name servers on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Examples:<br>
                            ns1.amazon.com<br>
                            ns2.amazon.org<br>
                            ns3.amazon.net<br>
                            ns4.amazon.co.uk
                    </div>
                </div>
                <div 
                    v-if="createRecordForm.type === 'SOA'"
                    class="text-gray-600 text-sm"
                >
                    <div class="flex-1">Start of authority record. Enter all time values in seconds.</div>
                    <div class="mt-2">
                        Format:<br>
                            [authority-domain] [hostmaster-email-address]<br>
                            [zone-serial-number] [refresh-time] [retry-time]<br>
                            [expire-time] [negative caching TTL]<br>
                        Example:<br>
                            ns.example.net. hostmaster.example.com. 1<br>
                            7200 900 1209600 86400
                    </div>
                </div>
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateRecordModal = false"
                        :disable="creatingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createRecord"
                        :loading="creatingRecord"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditRecordModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Edit Record Set</template>
            <template slot="body">
                <app-input
                    v-model="editRecordForm.name"
                    placeholder="Name"
                    :error="editRecordForm.errors.get('name')"
                    @updated="editRecordForm.errors.clear('name')"
                    required
                    disable
                />
                <app-select
                    v-model="editRecordForm.type"
                    placeholder="Type"
                    :error="editRecordForm.errors.get('type')"
                    @updated="editRecordForm.errors.clear('type')"
                    :options="recordTypeOptions"
                    label-key="label"
                    value-key="value"
                    required
                    disable
                />                
                <app-input
                    v-model="editRecordForm.ttl"
                    placeholder="TTL (seconds)"
                    :error="editRecordForm.errors.get('ttl')"
                    @updated="editRecordForm.errors.clear('ttl')"
                />
                <app-textarea
                    v-model="editRecordForm.content"
                    placeholder="Value"
                    :error="editRecordForm.errors.get('content')"
                    @updated="editRecordForm.errors.clear('content')"
                    required
                />
                <div 
                    v-if="editRecordForm.type === 'A'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">IPv4 address. Enter multiple addresses on separate lines.</div>
                    <div class="flex-1 ml-5">Example:<br>
                        192.0.2.235<br>
                        198.51.100.234</div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'CNAME'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name that you want to resolve to instead of the value in the Name field.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        www.example.com
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'MX'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">A priority and a domain name that specifies a mail server. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                        [priority] [mail server host name]<br>
                        Example:<br>
                        10 mailserver.example.com.<br>
                        20 mailserver2.example.com.
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'AAAA'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">IPv6 address. Enter multiple addresses on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        2001:0db8:85a3:0:0:8a2e:0370:7334<br>
                        fe80:0:0:0:202:b3ff:fe1e:8329
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'TXT'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">A text record. Enter multiple values on separate lines. Enclose text in quotation marks.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        "Sample Text Entries"<br>
                        "Enclose entries in quotation marks"
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'PTR'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name that you want to return.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        www.example.com
                    </div>
                </div>                    
                <div 
                    v-if="editRecordForm.type === 'SRV'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An SRV record. For information about SRV record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [priority] [weight] [port] [server host name]<br>
                        Example:<br>
                            1 10 5269 xmpp-server.example.com.<br>
                            2 12 5060 sip-server.example.com.
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'SPF'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An SPF record. For information about SPF record format, refer to the applicable documentation. Enter multiple values on separate lines. Enclose values in quotation marks.</div>
                    <div class="flex-1 ml-5">
                        Example:<br>
                        "v=spf1 ip4:192.168.0.1/16-all"
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'NAPTR'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">An NAPTR record. For information about NAPTR record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [order] [preference] [flags] [services] [regexp] [replacement]<br>
                        Example:<br>
                            100 100 "U" "" "!^.*$!sip:info@bar.example.com!" .<br>
                            10 100 "S" "SIP+D2U" "" foo.example.com.
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'CAA'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">Values that specify the certificate authorities that can issue certificates for this domain. Enter multiple values on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Format:<br>
                            [flag] [tag] [value]<br>
                        Examples:<br>
                            0 issue "caa.example.com"<br>
                            0 issuewild ";"
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'NS'"
                    class="text-gray-600 text-sm flex"
                >
                    <div class="flex-1">The domain name of a name server. Enter multiple name servers on separate lines.</div>
                    <div class="flex-1 ml-5">
                        Examples:<br>
                            ns1.amazon.com<br>
                            ns2.amazon.org<br>
                            ns3.amazon.net<br>
                            ns4.amazon.co.uk
                    </div>
                </div>
                <div 
                    v-if="editRecordForm.type === 'SOA'"
                    class="text-gray-600 text-sm"
                >
                    <div class="flex-1">Start of authority record. Enter all time values in seconds.</div>
                    <div class="mt-2">
                        Format:<br>
                            [authority-domain] [hostmaster-email-address]<br>
                            [zone-serial-number] [refresh-time] [retry-time]<br>
                            [expire-time] [negative caching TTL]<br>
                        Example:<br>
                            ns.example.net. hostmaster.example.com. 1<br>
                            7200 900 1209600 86400
                    </div>
                </div>          
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showEditRecordModal = false"
                        :disable="updatingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="updateRecord"
                        :loading="updatingRecord"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import Http from '@/helpers/Http';
    import Form from '@/helpers/Form';

    export default {
        props: {
            showTableHeader: {
                type: Boolean,
                default: true,
            },
            paginateTable: {
                type: Boolean,
                default: true,
            }
        },
        data() {
            return {
                tableIsLoading: false,
                refreshTable: false,
                refreshRecordsTable: false,
                showCreateModal: false,
                showRecordsModal: false,
                showEditRecordModal: false,
                showCreateRecordModal: false,
                creatingRecord: false,
                updatingRecord: false,
                creatingHostedZone: false,
                recordsAjaxParams: {},
                ajaxParams:{},
                selected: {},
                createForm: new Form({
                    id: null,
                    name: null,
                    comment: null,
                    delegation_set_id: null,
                    caller_reference: null,
                }),
                createRecordForm: new Form({                    
                    domain_id: null,
                    name: null,
                    content: null,
                    ttl: 300,
                    type: null,
                }),
                editRecordForm: new Form({
                    id: null,
                    name: null,
                    content: null,
                    ttl: 300,
                    type: null,
                }),
                delegationSets: [],
                recordTypeOptions: [
                    {
                        label: 'A – IPv4 address',
                        value: 'A',
                    },
                    {
                        label: 'CNAME – Canonical name',
                        value: 'CNAME',
                    },
                    {
                        label: 'MX – Mail exchange',
                        value: 'MX',
                    },
                    {
                        label: 'AAAA – IPv6 address',
                        value: 'AAAA',
                    },
                    {
                        label: 'TXT – Text',
                        value: 'TXT',
                    },
                    {
                        label: 'PTR – Pointer',
                        value: 'PTR',
                    },
                    {
                        label: 'SRV – Service locator',
                        value: 'SRV',
                    },
                    {
                        label: 'SPF – Sender Policy Framework',
                        value: 'SPF',
                    },
                    {
                        label: 'NAPTR – Name Authority Pointer',
                        value: 'NAPTR',
                    },
                    {
                        label: 'CAA – Certification Authority Authorization',
                        value: 'CAA',
                    },
                    {
                        label: 'NS – Name server',
                        value: 'NS',
                    },
                    {
                        label: 'SOA – Start of authority',
                        value: 'SOA',
                    },
                ],
                typeOptions: [
                    {
                        label: 'Public Hosted Zone',
                        value: 'public',
                    },
                    {
                        label: 'Private Hosted Zone for Amazon VPC',
                        value: 'private'
                    }
                ],
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Type',
                        field: 'type',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Account',
                        field: 'account',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Route53',
                        field: 'route53',
                        sorter: 'number',
                        minWidth: 150,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return `<span class='badge badge-green'>Migrated</span>`;
                            }

                            return `<span class='badge badge-red'>Not Migrated</span>`;
                            }
                    }
                ],
                actions: [
                    {
                        label: 'Migrate to Route53',
                        icon: () => import('@/icons/SettingsInputHDMIOutlined.vue'),
                        handler: items => {
                            this.showCreateModal = true;
                            this.createForm.reset();
                            this.createForm.errors.clear();
                            this.createForm.set({
                                id: items[0].id,
                                name: items[0].name,
                                delegation_set_id: this.delegationSets[0].id
                            });
                        }
                    },
                    {
                        label: 'Records',
                        icon: () => import('@/icons/AssignmentOutlined.vue'),
                        handler: items => {
                            this.selected = items[0];
                            this.showRecordsModal = true;
                            this.recordsAjaxParams = {domain_id: this.selected.id}
                        }
                    }
                ],
                recordsActions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.selected = selected[0];
                            this.showEditRecordModal = true;
                            this.editRecordForm.reset();
                            this.editRecordForm.errors.clear();
                            this.editRecordForm.set({
                                id: this.selected.id,
                                name: this.selected.name,
                                type: this.selected.type,
                                content: this.selected.content,
                                ttl: this.selected.ttl,
                            });
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: items => {
                            this.showCreateRecordModal = true;
                            this.createRecordForm.reset();
                            this.createRecordForm.errors.clear();
                            this.createRecordForm.set({
                                name: items[0].name,
                                domain_id: items[0].domain_id,
                            });
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.deleteRecord(selected[0]);
                        }
                    },
                ]
            }
        },
        methods: {
            createHostedZone() {
                this.creatingHostedZone = true;
                Http.post('powerdns/domains/migrate', this.createForm.payload())
                    .then(() => {
                        this.creatingHostedZone = false;
                        this.showCreateModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Domain was successfully migrated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingHostedZone = false;
                        this.createForm.errors.record(response.data);
                    });
            },
            createRecord() {
                this.creatingRecord = true;
                this.createRecordForm.errors.clear();
                Http.post('powerdns/records', this.createRecordForm.payload())
                    .then(() => {
                        this.creatingRecord = false;
                        this.refreshRecordsTable = true;
                        this.showCreateRecordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully created.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingRecord = false;
                        this.createRecordForm.errors.record(response.data);
                    });
            },
            updateRecord() {
                this.updatingRecord = true;
                this.editRecordForm.errors.clear();
                Http.patch(`powerdns/records/${this.editRecordForm.id}`, this.editRecordForm.payload())
                    .then(() => {
                        this.updatingRecord = false;
                        this.refreshRecordsTable = true;
                        this.showEditRecordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully updated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.updatingRecord = false;
                        this.editRecordForm.errors.record(response.data);
                    });
            },
            deleteRecord(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete record "<strong>${selected.name}</strong>" permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`powerdns/records/${selected.id}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshRecordsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully deleted.'
                        })
                    }
                });
            },
            handleAjaxResponse(response) {
                this.delegationSets = response.delegation_sets;
            },
        }
    }
</script>